import { BaseGridRenderer, RenderConfig } from './BaseGridRenderer';
import { IsometricGrid } from './IsometricGrid';
import { Cell, CellType, CellState } from './Cell';
import { IsometricMath, Point } from './IsometricMath';

export class IsometricGridRenderer extends BaseGridRenderer {
  protected grid: IsometricGrid;
  private tileWidth: number = 0;
  private tileHeight: number = 0;
  private offsetX: number = 0;
  private offsetY: number = 0;

  constructor(grid: IsometricGrid, config: RenderConfig) {
    super(grid, config);
    this.grid = grid;
    this.resizeCanvas();
  }

  resizeCanvas(): void {
    this.tileWidth = IsometricMath.getTileWidth(this.cellSize);
    this.tileHeight = IsometricMath.getTileHeight(this.cellSize);

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    for (let row = 0; row < this.grid.rows; row++) {
      for (let col = 0; col < this.grid.cols; col++) {
        const screen = IsometricMath.cellToScreen(col, row, this.tileWidth, this.tileHeight);
        const corners = IsometricMath.getDiamondCorners(screen.x, screen.y, this.tileWidth, this.tileHeight);

        for (const corner of corners) {
          minX = Math.min(minX, corner.x);
          maxX = Math.max(maxX, corner.x);
          minY = Math.min(minY, corner.y);
          maxY = Math.max(maxY, corner.y);
        }
      }
    }

    const gridWidth = maxX - minX;
    const gridHeight = maxY - minY;

    const padding = this.cellSize;
    const width = Math.ceil(gridWidth + padding * 2);
    const height = Math.ceil(gridHeight + padding * 2);

    this.canvas.width = width * this.dpr;
    this.canvas.height = height * this.dpr;
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    this.offsetX = -minX + padding;
    this.offsetY = -minY + padding;
  }

  render(hoverCell?: Cell | null, previewType?: 'wall' | 'erase' | 'start' | 'goal' | null): void {
    this.clear();
    this.renderCells();
    if (hoverCell && previewType) {
      this.renderCellPreview(hoverCell, previewType);
      this.renderHoverHighlight(hoverCell);
    }
  }

  cellFromPoint(x: number, y: number): { row: number; col: number } | null {
    const adjustedX = x - this.offsetX;
    const adjustedY = y - this.offsetY;

    const cell = IsometricMath.screenToCell(adjustedX, adjustedY, this.tileWidth, this.tileHeight);

    if (this.grid.isInBounds(cell.row, cell.col)) {
      return { row: cell.row, col: cell.col };
    }

    return null;
  }

  private renderCells(): void {
    for (let row = 0; row < this.grid.rows; row++) {
      for (let col = 0; col < this.grid.cols; col++) {
        const cell = this.grid.getCellAt(row, col);
        this.renderCell(cell);
      }
    }
  }

  private renderCell(cell: Cell): void {
    const center = this.getCellCenter(cell);
    const corners = IsometricMath.getDiamondCorners(center.x, center.y, this.tileWidth, this.tileHeight);

    const color = this.getCellColor(cell);
    this.ctx.fillStyle = color;

    this.ctx.beginPath();
    this.ctx.moveTo(corners[0].x, corners[0].y);
    for (let i = 1; i < corners.length; i++) {
      this.ctx.lineTo(corners[i].x, corners[i].y);
    }
    this.ctx.closePath();
    this.ctx.fill();

    if (this.showGrid) {
      this.ctx.strokeStyle = this.colors.grid;
      this.ctx.lineWidth = 1;
      this.ctx.stroke();
    }

    if (cell.type === CellType.START || cell.type === CellType.GOAL) {
      this.renderMarker(cell);
    }
  }

  private getCellColor(cell: Cell): string {
    switch (cell.type) {
      case CellType.WALL:
        return this.colors.wall;
      case CellType.START:
        return this.colors.start;
      case CellType.GOAL:
        return this.colors.goal;
      default:
        switch (cell.state) {
          case CellState.OPEN:
            return this.colors.open;
          case CellState.CLOSED:
            return this.colors.closed;
          case CellState.PATH:
            return this.colors.path;
          default:
            return this.colors.empty;
        }
    }
  }

  private renderMarker(cell: Cell): void {
    const center = this.getCellCenter(cell);
    const radius = this.cellSize / 3;

    this.ctx.fillStyle = '#ffffff';
    this.ctx.beginPath();
    this.ctx.arc(center.x, center.y, radius, 0, Math.PI * 2);
    this.ctx.fill();
  }

  private getCellCenter(cell: Cell): Point {
    const screen = IsometricMath.cellToScreen(cell.col, cell.row, this.tileWidth, this.tileHeight);
    return {
      x: screen.x + this.offsetX,
      y: screen.y + this.offsetY
    };
  }

  private renderCellPreview(cell: Cell, previewType: 'wall' | 'erase' | 'start' | 'goal'): void {
    const center = this.getCellCenter(cell);
    const corners = IsometricMath.getDiamondCorners(center.x, center.y, this.tileWidth, this.tileHeight);

    this.ctx.globalAlpha = 0.5;

    let shouldRender = false;
    let fillColor = '';

    switch (previewType) {
      case 'wall':
        if (cell.type !== CellType.START && cell.type !== CellType.GOAL) {
          shouldRender = true;
          fillColor = this.colors.wall;
        }
        break;

      case 'erase':
        if (cell.type === CellType.WALL) {
          shouldRender = true;
          fillColor = this.colors.empty;
        }
        break;

      case 'start':
        shouldRender = true;
        fillColor = this.colors.start;
        break;

      case 'goal':
        shouldRender = true;
        fillColor = this.colors.goal;
        break;
    }

    if (shouldRender) {
      this.ctx.fillStyle = fillColor;
      this.ctx.beginPath();
      this.ctx.moveTo(corners[0].x, corners[0].y);
      for (let i = 1; i < corners.length; i++) {
        this.ctx.lineTo(corners[i].x, corners[i].y);
      }
      this.ctx.closePath();
      this.ctx.fill();

      if (previewType === 'start' || previewType === 'goal') {
        this.ctx.globalAlpha = 1.0;
        this.ctx.fillStyle = '#ffffff';
        this.ctx.beginPath();
        this.ctx.arc(center.x, center.y, this.cellSize / 3, 0, Math.PI * 2);
        this.ctx.fill();
        this.ctx.globalAlpha = 0.5;
      }
    }

    this.ctx.globalAlpha = 1.0;
  }

  private renderHoverHighlight(cell: Cell): void {
    const center = this.getCellCenter(cell);
    const corners = IsometricMath.getDiamondCorners(center.x, center.y, this.tileWidth, this.tileHeight);

    this.ctx.strokeStyle = '#ffd700';
    this.ctx.lineWidth = 2;
    this.ctx.beginPath();
    this.ctx.moveTo(corners[0].x, corners[0].y);
    for (let i = 1; i < corners.length; i++) {
      this.ctx.lineTo(corners[i].x, corners[i].y);
    }
    this.ctx.closePath();
    this.ctx.stroke();
  }

  protected getCellCenterX(cell: Cell): number {
    return this.getCellCenter(cell).x;
  }

  protected getCellCenterY(cell: Cell): number {
    return this.getCellCenter(cell).y;
  }
}
